import React, { useState, useContext } from "react";
import { Link, withRouter } from "react-router-dom";
import moment from "moment";
import styled, { css } from "styled-components";
import { IsFeatureReady } from "src/constants/features";
import { UserContext } from "src/scenes/App/UserContext";
import IconBusiness from "src/img/new/business_dashboard.svg";
import IconMyMeApp from "src/img/new/my_me_app.svg";
import IconMyUserLists from "src/img/new/my_user_lists.svg";
import IconSalesAndPromotions from "src/img/new/sales_and_promotions.svg";
import IconNoticeBoard from "src/img/new/noticeboard.svg";
import IconChat from "src/img/new/chat.svg";
import IconDirectory from "src/img/new/directory.svg";
import IconPlaylists from "src/img/playlists icon@2x.png";
import IconSurveys from "src/img/new/surveys.svg";
import IconQuotes from "src/img/new/quotes.svg";
import IconPerks from "src/img/new/star.svg";
import IconDonations from "src/img/new/heart.svg";
import IconStatistics from "src/img/new/statistics.svg";
import IconAccountSettings from "src/img/new/account_settings.svg";
import IconLogout from "src/img/new/logout.svg";
import IconHelpCenter from "src/img/new/help_centre.svg";
import IconAdvertising from "src/img/advertising/Megaphone_white.svg";
import IconDigitalMemberCard from "src/img/new/member_card.svg";
import BodyAndMind from "src/img/bodyAndMind/Body_&_Mind.svg";
import EmailCampaigns from "src/img/emailCampaigns/email.svg";
import CalendarIcon from "src/img/calendar-nav.png";
import { DashboardThemeToggle, Initials } from "src/components";
import { SubscriptionMode, UserKeys } from "src/constants/userDetails";
import * as PERMISSIONS from "src/constants/permissions";
import {
    accountOwnerEmployeeName,
    capitalizeString,
    generateNameInitials,
    leftNavTrimStringWithEllipsis
} from "src/utils/helpers";
import ProfileSettingsModal from "src/scenes/Chat/components/ProfileSettingsModal";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import { DefaultDirectoryFeatureName } from "src/scenes/Directory/Directory";
import { DefaultMyQuotesFeatureName } from "src/scenes/MyQuotes/Myquotes";
import { DefaultBodyAndMindFeatureName } from "src/scenes/BodyAndMind/BodyAndMindList";
import { getSalesAndPromotionHeading } from "src/scenes/ClientLicense/components/SalesAndPromotionHeader";
import { getMembershipCardHeader } from "src/scenes/DigitalMemberCard";
import { getSurveyPollFeatureContext } from "src/scenes/Survey/components/Utils";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";
import { getAppDetails } from "src/config/appVariants";

const smallDevice = (...args) => css`
    @media (max-width: 1280px) {
        ${css(...args)}
    }
`;

const Greetings = styled.div`
    width: 270px;
    height: 52px;
    position: relative;
    display: flex;
    align-items: center;
    margin: 20px 0 20px 0;

    ${smallDevice`
        width: 75px;
    `}
`;

const SideBar = styled.div`
    --left-nav-width: 270px;
    --left-nav-collapsed-width: 85px;
    --left-nav-item-margin: 12px;

    margin-top: 0;
    height: 100%;
    z-index: 999;
    width: var(--left-nav-width);
    position: fixed;
    overflow-y: hidden;
    overflow-x: hidden;
    background: #11141A;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 18px 0 42px rgba(12, 18, 38, 0.18);
    box-sizing: border-box;

    ::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
        background-color: #F5F5F5;
    }

    ::-webkit-scrollbar {
        width: 5px;
        background-color: #736565;
    }

    ::-webkit-scrollbar-thumb {
        background-color: rgba(156, 156, 156, 0.2);
    }

    ${smallDevice`
        width: var(--left-nav-collapsed-width);
        transition: all 0.2s ease-in-out;

        &:hover {
            width: var(--left-nav-width);
        }

        &:hover ${Greetings} {
            width: 270px;
        }

        &:hover ${Greetings} > div {
            margin-right: 20px;
        }
    `}
`;

const ScrollerParent = styled.div`
    max-width: 270px;
    position: relative;
    min-height: 90%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    height: 96vh;
`;

const NavMenuTextWrapper = styled.div`
    height: 20px;
    margin: 0 20px 0 20px;
    padding: 8px 0;
`;

const CustomScroll = styled.div`
    flex-grow: 1;
    overflow-y: hidden;
    overflow-x: hidden;

    &:hover {
        overflow-y: auto;
    }
`;

const LoggedInLink = styled(Link)`
    font-family: Manrope, Helvetica, sans-serif;
    position: relative;
    display: ${p => p.display};
    align-items: center;
    padding: ${p => p.padding || "0 14px"};
    height: 32px;
    width: calc(var(--left-nav-width) - (var(--left-nav-item-margin) * 2));
    color: white;
    margin: ${p => p.margintop || "2px"} var(--left-nav-item-margin) 2px;
    text-decoration: none;
    font-size: 12px;
    font-weight: 700;
    border: 1px solid transparent;
    border-radius: 8px;
    box-sizing: border-box;
    cursor: ${p => p.isclickable === "true" ? "pointer" : "default"};

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.08);
    }

    ${p => Boolean(p.ishighlighted) && `
        background: rgba(255, 255, 255, 0.14);
        border-color: rgba(255, 255, 255, 0.22);
        color: #FFFFFF;
    `};
    ${p => p.isclickable === "true" ? "" : `
        pointer-events: none;
        color: #bdbdbd;
        opacity: 0.4;
    `};

    ${smallDevice`
        width: calc(var(--left-nav-collapsed-width) - (var(--left-nav-item-margin) * 2));
        justify-content: center;
        margin-right: auto;
        margin-left: auto;
        padding: 0;

        ${SideBar}:hover & {
            width: calc(var(--left-nav-width) - (var(--left-nav-item-margin) * 2));
            justify-content: flex-start;
            margin-right: var(--left-nav-item-margin);
            margin-left: var(--left-nav-item-margin);
            padding: ${p => p.padding || "0 14px"};
        }
    `}
`;

const LinkIcon = styled.img`
    height: 14px;
    width: 14px;
    padding-right: 12px;
    color: white;

    ${smallDevice`
        padding-right: 0;

        ${SideBar}:hover & {
            padding-right: 12px;
        }
    `}
`;

const UnreadBadge = styled.span`
    min-width: 18px;
    height: 18px;
    margin-left: auto;
    margin-right: 14px;
    padding: 0 5px;
    border-radius: 9px;
    background: #F44336;
    color: #FFFFFF;
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    box-sizing: border-box;

    ${smallDevice`
        position: absolute;
        top: 6px;
        left: 42px; /* beside the icon */
        z-index: 999;

        ${SideBar}:hover & {
            position: relative;
            top: auto;
            left: auto;
        }
    `}
`;

const HideTextOnSmall = styled.div`
    ${p => p.$isNavMenuText && `
        font-family: Manrope, Helvetica, sans-serif;
        font-weight: 500;
        font-size: 12px;
        color: #fff;
        opacity: 0.52;
        border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        width: 100px;
    `}

    ${p => p.$isGreetingText && `
        color: #fff;
        font-family: Manrope, Helvetica, sans-serif;
        font-size: 16px;
        font-weight: 700;
        letter-spacing: 0;
    `}

    ${smallDevice`
        opacity: 0 !important;
        position: absolute;
        ${SideBar}:hover & {
            position: relative;
            transform: translateX(0);
            opacity: ${p => p.$isNavMenuText ? "0.6 !important" : "1 !important"};
        }
    `}

`;

const FooterText = styled(HideTextOnSmall)`
    margin: 0 0 60px 27px;

    a {
        text-decoration: none;
        margin: 0 0 10px 0;
        color: #fff;
        font-family: Manrope, Helvetica, sans-serif;
        font-size: 12px;
        font-weight: 700;
        display: block;
    }
`;

const ThemeToggleRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.75rem;
    width: calc(var(--left-nav-width) - (var(--left-nav-item-margin) * 2));
    margin: 0 var(--left-nav-item-margin) 18px;
    padding: 0.75rem 0.85rem;
    box-sizing: border-box;
    min-height: 4.75rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);

    ${smallDevice`
        width: calc(var(--left-nav-collapsed-width) - (var(--left-nav-item-margin) * 2));
        grid-template-columns: 1fr;
        justify-items: center;
        margin-right: auto;
        margin-left: auto;
        min-height: 3rem;
        padding: 0;
        border-color: transparent;
        background: transparent;
        box-shadow: none;

        > button {
            border-color: transparent;
            background: transparent;
            overflow: hidden;
        }

        > button > div {
            transform: scale(0.72);
        }

        > button:hover {
            border-color: rgba(255, 255, 255, 0.16);
            background: rgba(255, 255, 255, 0.08);
        }

        ${SideBar}:hover & {
            width: calc(var(--left-nav-width) - (var(--left-nav-item-margin) * 2));
            grid-template-columns: minmax(0, 1fr) auto;
            justify-items: stretch;
            margin-right: var(--left-nav-item-margin);
            margin-left: var(--left-nav-item-margin);
            min-height: 4.75rem;
            padding: 0.75rem 0.85rem;
            border-color: rgba(255, 255, 255, 0.1);
            background: rgba(255, 255, 255, 0.06);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
        }

        ${SideBar}:hover & > button {
            border-color: rgba(255, 255, 255, 0.16);
            background: rgba(255, 255, 255, 0.08);
            overflow: visible;
        }

        ${SideBar}:hover & > button > div {
            transform: none;
        }
    `}
`;

const ThemeToggleCopy = styled(HideTextOnSmall)`
    display: grid;
    gap: 0.25rem;
    min-width: 0;
`;

const ThemeToggleTitle = styled.div`
    color: #fff;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 800;
    line-height: 1.2;
`;

const ThemeToggleSubtitle = styled.div`
    color: rgba(255, 255, 255, 0.62);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.25;
`;

function LeftNavigation(props) {
    const [sideProfileModalIsShown, setSideProfileModalIsShown] = useState(false);
    const { location, user } = props;
    const userContext = useContext(UserContext);
    const app = getAppDetails(userContext?.[UserKeys.APP_VARIANT]);
    const chatContext = useContext(ChatContext) || {};
    const matrixUnreadCount = Number(chatContext.matrixUnreadCount) || 0;
    const {
        permissions = [],
        firstName = "",
        lastName = "",
        isCustomizeComplete = false,
        companyName = null,
        profilePictureUrl,
        [UserKeys.SUBSCRIPTION_MODE]: subscriptionMode,
        [UserKeys.SUBSCRIPTION_WAS_EVER_PAID]: subscriptionWasEverPaid
    } = userContext || {};

    const mustSubscribe = subscriptionMode === SubscriptionMode.ACTIVE && subscriptionWasEverPaid !== true;
    const dashboardIsFullyAccessible = Boolean(isCustomizeComplete) && !mustSubscribe;
    const salesAndPromotionHeading = getSalesAndPromotionHeading(user);
    const digitalMemberCardHeading = getMembershipCardHeader(user);
    const surveyPollFeature = getSurveyPollFeatureContext(user?.featureFlags);

    /**
     * Using straight booleans as attribute values in components causes errors to be logged. A simple workaround is to
     * use integer values that will evaluate to truthy/falsy later. Discussed on a Stack Overflow thread:
     * https://stackoverflow.com/questions/31163693/how-do-i-conditionally-add-attributes-to-react-components
     * @param {boolean} boolValue
     * @returns {number}
     */
    const asInteger = (boolValue) => boolValue ? 1 : 0;

    const currentPageIs = (path) => {
        const SETTINGS_SUB_MENU = ["/setting", "/renewal", "/setting/business", "/setting/security", "/setting/billingHistory", "/setting/subscriptions", "/setting/payment", "/setting/renewalsAndEarnings"];

        if (path === "/setting") {
            return asInteger(SETTINGS_SUB_MENU.includes(location.pathname));
        } else {
            return asInteger(location.pathname === path);
        }
    };

    const greeting = (() => {
        let now = moment().format("HH");
        let greeting = "Good ";
        let splitAfternoon = 12;
        let splitEvening = 17;
        if (now >= splitAfternoon && now < splitEvening) {
            greeting += "afternoon";
        } else if (now >= splitEvening) {
            greeting += "evening";
        } else {
            greeting += "morning";
        }
        return greeting;
    });

    const logout = (() => {
        props.logout();
    });

    const openHelpCentre = () => {
        window.open("https://www.memotivationapp.com/help-centre", "_blank");
    };

    const canViewFeature = (featureFlag) => {
        if (!featureFlag) {
            return true;
        }

        if (Array.isArray(featureFlag)) {
            return featureFlag.some((flag) => hasFeature(user, flag));
        }

        return hasFeature(user, featureFlag);
    };

    const displayLink = (link) => {
        if (link.allowAuthenticated) {
            return canViewFeature(link.featureFlag);
        }

        const userPermissions = Array.isArray(permissions) ? permissions : [];
        const hasRequiredPermissions = link.allowAny
            ? link.allowed.some((val) => userPermissions.includes(val))
            : link.allowed.every((val) => userPermissions.includes(val));
        return hasRequiredPermissions && canViewFeature(link.featureFlag);
    };

    const links = [];
    links.push({
        name: "Business Dashboard", icon: IconBusiness, route: "/active",
        isClickable: true, allowAuthenticated: true
    });
    links.push({
        name: "My Me App", icon: IconMyMeApp, route: "/customise",
        isClickable: Boolean(companyName !== null), allowed: [PERMISSIONS.MY_ME_APP],
        featureFlag: FEATURE_FLAGS.MY_ME_APP
    });
    links.push({
        name: "My User Lists", icon: IconMyUserLists, route: "/employList",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.USER_LISTS],
        featureFlag: FEATURE_FLAGS.USER_LISTS
    });
    links.push({
        name: user[UserKeys.CUSTOM_NAME_SALES_AND_PROMOTIONS] || salesAndPromotionHeading, icon: IconSalesAndPromotions, route: "/clientLicense",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.SALES_AND_PROMOTIONS]
    });
    links.push({
        name: "Noticeboard", icon: IconNoticeBoard, route: "/noticeboard",
        isClickable: dashboardIsFullyAccessible, allowAuthenticated: true,
        featureFlag: FEATURE_FLAGS.NOTICEBOARD
    });
    links.push({
        name: "Calendar", icon: CalendarIcon, route: "/calendar",
        isClickable: true, allowAuthenticated: true
    });
    links.push({
        name: "Chat", icon: IconChat, route: "/chats",
        isClickable: dashboardIsFullyAccessible,
        allowAuthenticated: true,
        featureFlag: FEATURE_FLAGS.BUSINESS_CHAT,
        unreadCount: matrixUnreadCount
    });
    links.push({
        name: user[UserKeys.CUSTOM_NAME_MY_QUOTES] || DefaultMyQuotesFeatureName, icon: IconQuotes, route: "/myquotes",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.MY_QUOTE],
        featureFlag: FEATURE_FLAGS.QUOTES
    });
    links.push({
        name: user[UserKeys.CUSTOM_NAME_BODY_AND_MIND] || DefaultBodyAndMindFeatureName, icon: BodyAndMind, route: "/body-and-mind",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.BODY_AND_MIND],
        featureFlag: FEATURE_FLAGS.BODY_AND_MIND
    });
    links.push({
        name: "Playlists", icon: IconPlaylists, route: "/playlists",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.CREATE_BUSINESS_PLAYLISTS],
        featureFlag: FEATURE_FLAGS.PLAYLISTS
    });
    links.push({
        name: surveyPollFeature.title, icon: IconSurveys, route: "/survey",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.SURVEYS, PERMISSIONS.POLLS],
        allowAny: true,
        featureFlag: [FEATURE_FLAGS.SURVEYS, FEATURE_FLAGS.POLLS]
    });

    links.push({
        name: user[UserKeys.CUSTOM_NAME_DIRECTORY] || DefaultDirectoryFeatureName, icon: IconDirectory, route: "/directory",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.BUSINESS_DIRECTORY],
        featureFlag: FEATURE_FLAGS.BUSINESS_DIRECTORY
    });

    links.push({
        name: "Perks", icon: IconPerks, route: "/perks",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.PERKS],
        featureFlag: FEATURE_FLAGS.PERKS
    });

    links.push({
        name: "Donations", icon: IconDonations, route: "/donations",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.DONATIONS],
        featureFlag: FEATURE_FLAGS.DONATIONS
    });

    links.push({
        name: "Email Campaigns", icon: EmailCampaigns, route: "/email-campaigns",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.EMAIL_CAMPAIGNS],
        featureFlag: FEATURE_FLAGS.EMAIL_CAMPAIGNS
    });

    links.push({
        name: "Advertising", icon: IconAdvertising, route: "/advertising",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.ADVERTISING],
        featureFlag: FEATURE_FLAGS.ADVERTISING
    });

    links.push({
        name: user[UserKeys.CUSTOM_NAME_DIGITAL_MEMBER_CARDS] || digitalMemberCardHeading, icon: IconDigitalMemberCard, route: "/digitalMemberCards",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.DIGITAL_MEMBER_CARDS],
        featureFlag: FEATURE_FLAGS.DIGITAL_MEMBER_CARDS
    });

    links.push({
        name: "Statistics", icon: IconStatistics, route: "/statistics",
        isClickable: dashboardIsFullyAccessible, allowed: [PERMISSIONS.STATISTICS],
        featureFlag: FEATURE_FLAGS.STATISTICS
    });
    links.push({
        name: "Account Settings", icon: IconAccountSettings, route: "/setting",
        isClickable: true, allowAuthenticated: true
    });

    const navItems = () => {
        return links.map((link, index) => {
            return (
                <LoggedInLink to={link.route} margintop="0" key={index}
                    ishighlighted={currentPageIs(link.route)}
                    isclickable={link.isClickable.toString()}
                    display={displayLink(link) ? "flex" : "none"}
                >
                    <LinkIcon src={link.icon}></LinkIcon>
                    <HideTextOnSmall>{link.name}</HideTextOnSmall>
                    {link.unreadCount > 0 &&
                        <UnreadBadge>{link.unreadCount > 99 ? "99+" : link.unreadCount}</UnreadBadge>
                    }
                </LoggedInLink>
            );
        });
    };

    const hasAccountOwnerEmployeeName =
        Boolean(accountOwnerEmployeeName.firstName) || Boolean(accountOwnerEmployeeName.lastName);
    const useAccountOwnerEmployeeName =
        permissions.includes(PERMISSIONS.ACCOUNT_OWNER_PERMISSION) && hasAccountOwnerEmployeeName;
    const displayFirstName = useAccountOwnerEmployeeName ? accountOwnerEmployeeName.firstName : firstName;
    const displayLastName = useAccountOwnerEmployeeName ? accountOwnerEmployeeName.lastName : lastName;
    const firstNameLastName = capitalizeString(displayFirstName) + " " +
        (displayLastName ? capitalizeString(displayLastName) : "");

    return (
        <SideBar>
            <ScrollerParent>
                <Greetings>
                    <Initials profilePictureUrl={profilePictureUrl} onClick={() => setSideProfileModalIsShown(true)} >
                        <div>{!profilePictureUrl ? generateNameInitials(displayFirstName, displayLastName) : ""}</div>
                    </Initials>
                    <HideTextOnSmall $isGreetingText style={{ maxWidth: "170px" }}>
                        {greeting()}, <br /> {leftNavTrimStringWithEllipsis(firstNameLastName)}
                    </HideTextOnSmall>
                </Greetings>
                {sideProfileModalIsShown &&
                    <ProfileSettingsModal
                        handleClose={() => setSideProfileModalIsShown(false)}
                        isCurrentUser={true}
                        userToShow={userContext}
                        profileModalUserLeadershipRoles={userContext.leadershipRoles}
                    />
                }
                <NavMenuTextWrapper>
                    <HideTextOnSmall $isNavMenuText>Main Menu</HideTextOnSmall>
                </NavMenuTextWrapper>

                <CustomScroll>
                    {navItems()}

                    <LoggedInLink to="?open=help_centre"
                        isclickable="true"
                        display="flex"
                        onClick={openHelpCentre}
                        margintop="0"
                    >
                        <LinkIcon src={IconHelpCenter}></LinkIcon>
                        <HideTextOnSmall>Help Centre</HideTextOnSmall>
                    </LoggedInLink>

                    <LoggedInLink to="/login"
                        ishighlighted={0}
                        isclickable="true"
                        style={{ marginBottom: "20px" }}
                        onClick={logout}
                        display="flex"
                    >
                        <LinkIcon src={IconLogout}></LinkIcon>
                        <HideTextOnSmall>Log Out</HideTextOnSmall>
                    </LoggedInLink>
                    {IsFeatureReady.darkMode() &&
                        <ThemeToggleRow>
                            <ThemeToggleCopy>
                                <ThemeToggleTitle>Theme</ThemeToggleTitle>
                                <ThemeToggleSubtitle>Light / Dark Mode</ThemeToggleSubtitle>
                            </ThemeToggleCopy>
                            <DashboardThemeToggle compact nav />
                        </ThemeToggleRow>
                    }
                    <FooterText>
                        <a href={app.termsUrl} target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
                        <a href={app.privacyUrl} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                        <a href="https://www.memotivationapp.com/privacy-policy" target="_blank" rel="noopener noreferrer">Cookie Policy</a>
                    </FooterText>
                </CustomScroll>
            </ScrollerParent>
        </SideBar>
    );
}

export default withRouter(LeftNavigation);
